import { useCallback, useMemo } from 'react';

import { usePeersonaFeatureFlagEnabledForClient } from '../api/useFeatureFlag';

const LISTENER_ROLE_ENV_KEYS = ['REACT_APP_PEERSONA_1', 'REACT_APP_PEERSONA_2', 'REACT_APP_PEERSONA_3'] as const;

/** Listener role ids allowed to surface Peersona UI in this build (from env at compile time). */
export function parsePeersonaListenerRoleAllowlist(): Set<number> {
  const ids = LISTENER_ROLE_ENV_KEYS.map((key) => process.env[key])
    .filter((value): value is string => !!value && value.trim() !== '')
    .map((value) => Number(value))
    .filter((n) => Number.isFinite(n) && n > 0);
  return new Set(ids);
}

/**
 * Peersona is gated by the PEERSONA feature flag (client-scoped when configured)
 * and by REACT_APP_PEERSONA_1/2/3 listener_role_id allowlist per environment.
 */
export function usePeersonaAccess() {
  const featureEnabled = usePeersonaFeatureFlagEnabledForClient();
  const allowlist = useMemo(() => parsePeersonaListenerRoleAllowlist(), []);

  const isPeerPeersonaEnabled = useCallback(
    (listenerRoleId: number | null | undefined, chatbotId: number | null | undefined): boolean => {
      if (!featureEnabled) return false;
      if (!chatbotId) return false;
      if (allowlist.size === 0) return false;
      if (listenerRoleId == null) return false;
      return allowlist.has(listenerRoleId);
    },
    [featureEnabled, allowlist],
  );

  /**
   * Lighter-weight allowlist check for surfaces that only show a Peersona
   * affordance (e.g. a sparkle next to the peer name on a tile). Actual
   * chat entry still flows through ``isPeerPeersonaEnabled`` on the
   * profile page, which also requires a loaded chatbot id.
   */
  const isPeerPeersonaAllowlisted = useCallback(
    (listenerRoleId: number | null | undefined): boolean => {
      if (!featureEnabled) return false;
      if (allowlist.size === 0) return false;
      if (listenerRoleId == null) return false;
      return allowlist.has(listenerRoleId);
    },
    [featureEnabled, allowlist],
  );

  return { featureEnabled, isPeerPeersonaEnabled, isPeerPeersonaAllowlisted };
}
